"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { X, Save, Loader2, RotateCcw, Hash, FileText, Tag } from "lucide-react"
import {
  Button,
  Input,
  Label,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@mantis-sas/ui";
import { 
  getSKUConfig, 
  updateSKUConfig 
} from "../../../../ApiCalls/mantisFlowsSkus"

export default function SKUConfigModal({ clientId, onClose, embedded = false }) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState({
    prefix: "",
    include_category: true,
    include_subcategory: true,
    use_numeric_sequence: true,
    sequence_length: 4,
    sequence_start: 1001,
    separator: "-"
  })
  
  const { toast } = useToast()
  
  // Load existing SKU configuration
  useEffect(() => {
    loadSKUConfig()
    
    // Add event listener for loading state
    window.addEventListener('skuConfigLoading', handleLoadingEvent)
    window.addEventListener('skuConfigUpdating', handleSavingEvent)
    
    return () => {
      window.removeEventListener('skuConfigLoading', handleLoadingEvent)
      window.removeEventListener('skuConfigUpdating', handleSavingEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  
  // Handle loading state event
  const handleLoadingEvent = (event) => {
    setLoading(event.detail)
  }
  
  // Handle saving state event
  const handleSavingEvent = (event) => {
    setSaving(event.detail)
  }
  
  // Load SKU configuration from API
  const loadSKUConfig = async () => {
    try {
      setLoading(true)
      const response = await getSKUConfig(clientId)
      
      if (response.success && response.data) {
        setConfig({
          prefix: response.data.prefix || "",
          include_category: response.data.include_category !== false,
          include_subcategory: response.data.include_subcategory !== false,
          use_numeric_sequence: response.data.use_numeric_sequence !== false,
          sequence_length: response.data.sequence_length || 4,
          sequence_start: response.data.sequence_start || 1001,
          separator: response.data.separator || "-"
        })
      }
    } catch (error) {
      console.error("Error loading SKU configuration:", error)
      toast({
        title: "Error",
        description: "Failed to load SKU configuration",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }
  
  // Handle form input changes
  const handleChange = (name, value) => {
    setConfig(prev => ({
      ...prev,
      [name]: value
    }))
  }
  
  // Reset configuration to defaults
  const handleReset = () => {
    setConfig({
      prefix: "",
      include_category: true,
      include_subcategory: true,
      use_numeric_sequence: true,
      sequence_length: 4,
      sequence_start: 1001,
      separator: "-"
    })
  }
  
  // Save configuration
  const handleSave = async () => {
    try {
      setSaving(true)
      const response = await updateSKUConfig(clientId, config)
      
      if (response.success) {
        toast({
          title: "Success",
          description: "SKU configuration saved successfully",
          variant: "default"
        })
        if (!embedded) {
          onClose()
        }
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to save SKU configuration",
          variant: "destructive"
        })
      }
    } catch (error) {
      console.error("Error saving SKU configuration:", error)
      toast({
        title: "Error",
        description: "Failed to save SKU configuration",
        variant: "destructive"
      })
    } finally {
      setSaving(false)
    }
  }
  
  // Generate SKU example
  const generateExample = () => {
    let example = config.prefix || ""
    
    if (config.include_category) {
      example += (example ? config.separator : "") + "CAT"
    }
    
    if (config.include_subcategory) {
      example += (example ? config.separator : "") + "SUB"
    }
    
    if (config.use_numeric_sequence) {
      const sequenceNum = config.sequence_start.toString().padStart(config.sequence_length, '0')
      example += (example ? config.separator : "") + sequenceNum
    }
    
    return example || "No format configured"
  }
  
  // If embedded, render just the content without modal wrapper
  if (embedded) {
    return (
      <div>
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Loader2 className="h-8 w-8 text-orange-500 animate-spin mb-4" />
            <p className="text-slate-600 dark:text-slate-400">Loading SKU configuration...</p>
          </div>
        ) : (
          <Tabs defaultValue="format">
            <TabsList className="mb-6">
              <TabsTrigger value="format" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                <Hash className="h-4 w-4 mr-2" />
                SKU Format
              </TabsTrigger>
              <TabsTrigger value="preview" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                <FileText className="h-4 w-4 mr-2" />
                Preview
              </TabsTrigger>
            </TabsList>
            
            {/* Format Tab */}
            <TabsContent value="format" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column */}
                <div className="space-y-4">
                  {/* Prefix */}
                  <div className="space-y-2">
                    <Label htmlFor="prefix" className="text-sm text-slate-700 dark:text-slate-300">
                      Prefix
                    </Label>
                    <Input
                      id="prefix"
                      value={config.prefix}
                      onChange={(e) => handleChange("prefix", e.target.value)}
                      placeholder="e.g. PROD"
                      className="bg-white dark:bg-slate-800"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Short text at the beginning of the SKU
                    </p>
                  </div>
                  
                  {/* Separator */}
                  <div className="space-y-2">
                    <Label htmlFor="separator" className="text-sm text-slate-700 dark:text-slate-300">
                      Separator
                    </Label>
                    <Input
                      id="separator"
                      value={config.separator}
                      onChange={(e) => handleChange("separator", e.target.value)}
                      placeholder="-"
                      maxLength={1}
                      className="bg-white dark:bg-slate-800"
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Character used to separate SKU parts
                    </p>
                  </div>
                  
                  {/* Include Category */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="include_category" className="text-sm text-slate-700 dark:text-slate-300">
                        Include Category
                      </Label>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Use category code in SKU
                      </p>
                    </div>
                    <Switch
                      id="include_category"
                      checked={config.include_category}
                      onCheckedChange={(checked) => handleChange("include_category", checked)}
                    />
                  </div>
                  
                  {/* Include Subcategory */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="include_subcategory" className="text-sm text-slate-700 dark:text-slate-300">
                        Include Subcategory
                      </Label>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Use subcategory code in SKU
                      </p>
                    </div>
                    <Switch
                      id="include_subcategory"
                      checked={config.include_subcategory}
                      onCheckedChange={(checked) => handleChange("include_subcategory", checked)}
                    />
                  </div>
                </div>
                
                {/* Right Column */}
                <div className="space-y-4">
                  {/* Use Numeric Sequence */}
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="use_numeric_sequence" className="text-sm text-slate-700 dark:text-slate-300">
                        Use Numeric Sequence
                      </Label>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Add sequential numbers to SKUs
                      </p>
                    </div>
                    <Switch
                      id="use_numeric_sequence"
                      checked={config.use_numeric_sequence}
                      onCheckedChange={(checked) => handleChange("use_numeric_sequence", checked)}
                    />
                  </div>
                  
                  {/* Sequence Length */}
                  {config.use_numeric_sequence && (
                    <div className="space-y-2">
                      <Label htmlFor="sequence_length" className="text-sm text-slate-700 dark:text-slate-300">
                        Sequence Length
                      </Label>
                      <Input
                        id="sequence_length"
                        type="number"
                        min={1}
                        max={10}
                        value={config.sequence_length}
                        onChange={(e) => handleChange("sequence_length", parseInt(e.target.value) || 4)}
                        className="bg-white dark:bg-slate-800"
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Number of digits in the sequence (will be padded with zeros)
                      </p>
                    </div>
                  )}
                  
                  {/* Sequence Start */}
                  {config.use_numeric_sequence && (
                    <div className="space-y-2">
                      <Label htmlFor="sequence_start" className="text-sm text-slate-700 dark:text-slate-300">
                        Sequence Start
                      </Label>
                      <Input
                        id="sequence_start"
                        type="number"
                        min={1}
                        value={config.sequence_start}
                        onChange={(e) => handleChange("sequence_start", parseInt(e.target.value) || 1001)}
                        className="bg-white dark:bg-slate-800"
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Starting number for the sequence
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            
            {/* Preview Tab */}
            <TabsContent value="preview" className="space-y-6">
              <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded border border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200 mb-4">SKU Example</h3>
                
                <div className="p-3 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 font-mono text-xl text-center">
                  {generateExample()}
                </div>
                
                <div className="mt-6 space-y-2">
                  <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Format explanation:</h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600 dark:text-slate-400">
                    {config.prefix && (
                      <li><span className="font-semibold">{config.prefix}</span> - Custom prefix</li>
                    )}
                    {config.include_category && (
                      <li><span className="font-semibold">CAT</span> - Category code (from item's category)</li>
                    )}
                    {config.include_subcategory && (
                      <li><span className="font-semibold">SUB</span> - Subcategory code (from item's subcategory)</li>
                    )}
                    {config.use_numeric_sequence && (
                      <li>
                        <span className="font-semibold">
                          {config.sequence_start.toString().padStart(config.sequence_length, '0')}
                        </span> - Sequential number (auto-incrementing)
                      </li>
                    )}
                    <li><span className="font-semibold">{config.separator}</span> - Separator between components</li>
                  </ul>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}
        
        <div className="mt-6 flex justify-between">
          <Button 
            variant="outline" 
            onClick={handleReset}
            disabled={loading || saving}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          
          <Button 
            onClick={handleSave}
            disabled={loading || saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Configuration
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }
  
  // Otherwise render as standalone modal
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="bg-white dark:bg-slate-900 rounded shadow-lg w-full max-w-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center">
            <Tag className="h-6 w-6 mr-2 text-orange-500" />
            SKU Configuration
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Loader2 className="h-8 w-8 text-orange-500 animate-spin mb-4" />
              <p className="text-slate-600 dark:text-slate-400">Loading SKU configuration...</p>
            </div>
          ) : (
            <Tabs defaultValue="format">
              <TabsList className="mb-6">
                <TabsTrigger value="format" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                  <Hash className="h-4 w-4 mr-2" />
                  SKU Format
                </TabsTrigger>
                <TabsTrigger value="preview" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white">
                  <FileText className="h-4 w-4 mr-2" />
                  Preview
                </TabsTrigger>
              </TabsList>
              
              {/* Format Tab */}
              <TabsContent value="format" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    {/* Prefix */}
                    <div className="space-y-2">
                      <Label htmlFor="prefix" className="text-sm text-slate-700 dark:text-slate-300">
                        Prefix
                      </Label>
                      <Input
                        id="prefix"
                        value={config.prefix}
                        onChange={(e) => handleChange("prefix", e.target.value)}
                        placeholder="e.g. PROD"
                        className="bg-white dark:bg-slate-800"
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Short text at the beginning of the SKU
                      </p>
                    </div>
                    
                    {/* Separator */}
                    <div className="space-y-2">
                      <Label htmlFor="separator" className="text-sm text-slate-700 dark:text-slate-300">
                        Separator
                      </Label>
                      <Input
                        id="separator"
                        value={config.separator}
                        onChange={(e) => handleChange("separator", e.target.value)}
                        placeholder="-"
                        maxLength={1}
                        className="bg-white dark:bg-slate-800"
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Character used to separate SKU parts
                      </p>
                    </div>
                    
                    {/* Include Category */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="include_category" className="text-sm text-slate-700 dark:text-slate-300">
                          Include Category
                        </Label>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Use category code in SKU
                        </p>
                      </div>
                      <Switch
                        id="include_category"
                        checked={config.include_category}
                        onCheckedChange={(checked) => handleChange("include_category", checked)}
                      />
                    </div>
                    
                    {/* Include Subcategory */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="include_subcategory" className="text-sm text-slate-700 dark:text-slate-300">
                          Include Subcategory
                        </Label>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Use subcategory code in SKU
                        </p>
                      </div>
                      <Switch
                        id="include_subcategory"
                        checked={config.include_subcategory}
                        onCheckedChange={(checked) => handleChange("include_subcategory", checked)}
                      />
                    </div>
                  </div>
                  
                  {/* Right Column */}
                  <div className="space-y-4">
                    {/* Use Numeric Sequence */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label htmlFor="use_numeric_sequence" className="text-sm text-slate-700 dark:text-slate-300">
                          Use Numeric Sequence
                        </Label>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Add sequential numbers to SKUs
                        </p>
                      </div>
                      <Switch
                        id="use_numeric_sequence"
                        checked={config.use_numeric_sequence}
                        onCheckedChange={(checked) => handleChange("use_numeric_sequence", checked)}
                      />
                    </div>
                    
                    {/* Sequence Length */}
                    {config.use_numeric_sequence && (
                      <div className="space-y-2">
                        <Label htmlFor="sequence_length" className="text-sm text-slate-700 dark:text-slate-300">
                          Sequence Length
                        </Label>
                        <Input
                          id="sequence_length"
                          type="number"
                          min={1}
                          max={10}
                          value={config.sequence_length}
                          onChange={(e) => handleChange("sequence_length", parseInt(e.target.value) || 4)}
                          className="bg-white dark:bg-slate-800"
                        />
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Number of digits in the sequence (will be padded with zeros)
                        </p>
                      </div>
                    )}
                    
                    {/* Sequence Start */}
                    {config.use_numeric_sequence && (
                      <div className="space-y-2">
                        <Label htmlFor="sequence_start" className="text-sm text-slate-700 dark:text-slate-300">
                          Sequence Start
                        </Label>
                        <Input
                          id="sequence_start"
                          type="number"
                          min={1}
                          value={config.sequence_start}
                          onChange={(e) => handleChange("sequence_start", parseInt(e.target.value) || 1001)}
                          className="bg-white dark:bg-slate-800"
                        />
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          Starting number for the sequence
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
              
              {/* Preview Tab */}
              <TabsContent value="preview" className="space-y-6">
                <div className="bg-slate-50 dark:bg-slate-800 p-6 rounded border border-slate-200 dark:border-slate-700">
                  <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200 mb-4">SKU Example</h3>
                  
                  <div className="p-3 bg-white dark:bg-slate-900 rounded border border-slate-200 dark:border-slate-700 font-mono text-xl text-center">
                    {generateExample()}
                  </div>
                  
                  <div className="mt-6 space-y-2">
                    <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Format explanation:</h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600 dark:text-slate-400">
                      {config.prefix && (
                        <li><span className="font-semibold">{config.prefix}</span> - Custom prefix</li>
                      )}
                      {config.include_category && (
                        <li><span className="font-semibold">CAT</span> - Category code (from item's category)</li>
                      )}
                      {config.include_subcategory && (
                        <li><span className="font-semibold">SUB</span> - Subcategory code (from item's subcategory)</li>
                      )}
                      {config.use_numeric_sequence && (
                        <li>
                          <span className="font-semibold">
                            {config.sequence_start.toString().padStart(config.sequence_length, '0')}
                          </span> - Sequential number (auto-incrementing)
                        </li>
                      )}
                      <li><span className="font-semibold">{config.separator}</span> - Separator between components</li>
                    </ul>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
        
        {/* Footer */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex justify-between">
          <Button 
            variant="outline" 
            onClick={handleReset}
            disabled={loading || saving}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            
            <Button 
              onClick={handleSave}
              disabled={loading || saving}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Configuration
                </>
              )}
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  )
} 